import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Space, Divider, Avatar, Row, Col, Layout, Typography, Tag, Table, Descriptions } from 'antd';
import { UserOutlined } from '@ant-design/icons';

import NivoCalendarChart from '../../../charts/nivo/NivoCalendarChart';  // Import the generalized Nivo chart component
import { format, differenceInDays } from 'date-fns';
import PageHeader from "../../../layouts/PageHeader";
import {iconMapping} from "../../../configs/AppsConfig";
import Button from '@mui/material/Button';
import TimeCell from "../../../components/TimeCell";
import {sampleColors, sampleModernColors, sampleChromaColors} from "../../../utils/colors";
// Function to transform Unix timestamp view history
const transformViewHistoryToCalendarData = (viewHistory) => {
  return viewHistory.map(view => ({
    value: 1,  // Each view counts as 1
    day: format(new Date(view.timestamp * 1000), 'yyyy-MM-dd'),  // Convert Unix timestamp to 'YYYY-MM-DD'
  }));
};

// Calculate total views and views in the last 7 days
const calculateStatistics = (viewHistory) => {
  const totalViews = viewHistory.length;
  const sevenDaysAgo = new Date().setDate(new Date().getDate() - 7);
  const last7DaysViews = viewHistory.filter(view => new Date(view.timestamp * 1000) >= sevenDaysAgo).length;

  return { totalViews, last7DaysViews };
};

const PageViewHistory = () => {
  const { pageId } = useParams(); // Get pageId from route
  const navigate = useNavigate();
  const pageDetails = useSelector((state) => state.user.data?.page_details);

  // Find the page with the matching pageId and get its views
  const page = pageDetails?.find((page) => page.page_id === pageId);
  const viewHistory = page?.pageViews || [];

  // Transform view history for the Nivo calendar chart
  const calendarData = useMemo(() => transformViewHistoryToCalendarData(viewHistory), [viewHistory]);

  // Calculate statistics for total views and views in the last 7 days
  const { totalViews, last7DaysViews } = calculateStatistics(viewHistory);

  const reverseSortedViewHistory = useMemo(() => [...viewHistory].sort((a, b) => b.timestamp - a.timestamp), [viewHistory]);


  const uniqueEmails = useMemo(() => {
    const emails = new Set();
    viewHistory.forEach(view => emails.add(view.email));
    return Array.from(emails);
  }, [page]);

  const colors = useMemo(() => {
      if (uniqueEmails.length === 0) {
          return
      }
      const sampled = sampleModernColors(uniqueEmails.length);
      const mapping = {};
      uniqueEmails.forEach((email, index) => {
            mapping[email] = sampled[index];
      });
      return mapping;

  }, [uniqueEmails]);



  // Table columns for detailed view history
  const columns = [
      {
          title: '',
          width: 55,
          align: 'middle',
          render: (record) => {
              return <Avatar size={'small'} icon={<UserOutlined />} style={{backgroundColor: colors[record.email]}} />
          }
      },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Date',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) => <TimeCell timestamp={timestamp} />,
    },
  ];

  return (
    <Layout>
        <PageHeader
            logoUrl={iconMapping.iq}
            title={
            <Typography.Text style={{fontSize: '20px'}}>
                Viewing History for <Typography.Link style={{fontSize: '20px'}}>{page?.title}</Typography.Link>
            </Typography.Text>
            }
            actions={[
                <Button color={'info'} onClick={()=>navigate('/pages')}>Back to Pages</Button>
            ]}
        />

        <Layout.Content style={{padding: '2.5%', margin: 'auto'}}>
                    {/* Display Nivo Calendar Chart */}
            <Space direction={'horizontal'}>
                <Button onClick={()=>navigate('/pages')} color={'info'}>
                    Back to Page Collection
                </Button>
            </Space>
            <Divider/>
            <Row align={'middle'} wrap={true} gutter={16}>
                    {uniqueEmails.length > 0 &&
                        uniqueEmails.map(email => (
                            <Col>
                                <Space>
                                    <Avatar size={'small'} key={email} icon={<UserOutlined />} style={{backgroundColor: colors[email]}} />
                                    <Typography.Text>{email}</Typography.Text>
                                </Space>
                            </Col>
                        )
                    )}
            </Row>
            <Divider/>
            <div style={{height: 200, width: '100vw', maxWidth: 1000}}>
                <NivoCalendarChart data={calendarData} from="2024-03-01" to={format(new Date(), 'yyyy-MM-dd')} />
            </div>
            <Table
              dataSource={reverseSortedViewHistory}
              columns={columns}
              rowKey={(record) => record.timestamp}
              style={{maxWidth: 1000}}
              pagination={{
                  pageSize: 50
              }}
              scroll={{
                  y: 600
              }}
            />
        </Layout.Content>
    </Layout>

  );
};

export default PageViewHistory;
